import { Chart, ArcElement } from '../models/chart.models';

/**
 * Custom plugin for Chart.js that modifies the rendering of a gauge chart.
 * Specifically adjusts the angles of certain elements in the dataset to achieve
 * a gauge-like visualization.
 */
export const gaugePlugin = {
  // Unique identifier for the plugin
  id: 'gaugePlugin',

  /**
   * Hook that executes after all datasets have been drawn on the chart.
   * This hook adjusts the angles of specific elements in the chart to create
   * the desired gauge effect.
   *
   * @param chart - The Chart.js instance currently being rendered. Provides
   *                access to datasets, elements, and the drawing context.
   */
  afterDatasetsDraw(chart: Chart) {
    // Retrieve the ArcElements from the first dataset
    const elements = chart.getDatasetMeta(0).data as ArcElement[];
    const { ctx } = chart; // Extract the drawing context from the chart instance

    // Select individual elements to modify
    const firstElement = elements[0]; // Reference to the first ArcElement
    const secondElement = elements[1]; // Reference to the second ArcElement

    // Adjust angles for the second ArcElement to form the gauge shape
    secondElement.startAngle = -Math.PI; // Set the starting angle to -π (leftmost edge)
    secondElement.endAngle = 0; // Set the ending angle to 0 (center of the chart)

    // Render the adjusted elements on the canvas
    secondElement.draw(ctx); // Redraw the second ArcElement with updated angles
    firstElement.draw(ctx); // Redraw the first ArcElement (if needed for consistency)
  },
};

/**
 * Plugin for customizing the appearance of a target pointer on a gauge chart.
 * This plugin draws a percentage value above a specific data point in the chart.
 */
export const gaugeTargetPointerPlugin = {
  id: 'gaugeTargetPointerPlugin', // Unique identifier for the plugin

  /**
   * Hook that runs after datasets are drawn on the chart.
   * @param chart - The chart instance containing the context, data, and layout information.
   */
  afterDatasetsDraw(chart: Chart) {
    const { ctx, chartArea } = chart; // Extract the rendering context and chart dimensions
    const { x, y } = chart.getDatasetMeta(0).data[0]; // Retrieve the position of the target point from the first dataset
    const { width, height } = chartArea; // Get the dimensions of the chart area

    // Calculate the font size dynamically to maintain responsiveness
    // Font size is a percentage of the chart width but constrained to a range
    const fontSize = Math.max(Math.min(width * 0.03, height * 0.05), 20); // Minimum 20px, maximum 5% of height

    // Calculate vertical offset for positioning the text above the target point
    const offset = fontSize * 1.5; // Offset scales with the font size to maintain proportional spacing

    ctx.save(); // Save the current drawing state

    // Set the font properties with the calculated font size
    ctx.font = `${fontSize}px 'Lato', sans-serif`;
    ctx.fillStyle = 'black'; // Set the text color
    ctx.textAlign = 'center'; // Center align the text horizontally
    ctx.textBaseline = 'middle'; // Align text vertically to its middle point

    // Draw the percentage text at the calculated position
    // Text is positioned directly above the target point, adjusted by the offset
    ctx.fillText('50,2%', x, y - offset);

    ctx.restore(); // Restore the previous drawing state to prevent style bleed
  },
};
